<template>
  <div class="search-panel">
    <!-- Header -->
    <div class="panel-header">
      <div class="header-left">
        <svg class="header-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <span class="header-title">Resultados</span>
      </div>
      <div class="header-right">
        <button
          class="filter-toggle-btn"
          :class="{ active: showFilters }"
          @click="showFilters = !showFilters"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/>
          </svg>
          <span class="filter-toggle-label">Filtrar</span>
          <span v-if="activeFilterCount" class="filter-badge">{{ activeFilterCount }}</span>
        </button>
        <button class="close-btn" @click="closeResults">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <transition name="filters-collapse">
      <div v-if="showFilters" class="filters-section">
        <div class="filter-row">
          <span class="filter-row-label">Estado</span>
          <button
            class="filter-chip"
            :class="{ active: selectedStatuses.includes('active') }"
            @click="toggleStatus('active')"
          >
            Activas
          </button>
          <button
            class="filter-chip"
            :class="{ active: selectedStatuses.includes('completed') }"
            @click="toggleStatus('completed')"
          >
            Completadas
          </button>
          <button
            class="filter-clear-btn"
            :disabled="!selectedStatuses.length"
            @click="clearStatuses"
          >
            Limpiar
          </button>
        </div>

        <div class="filter-row">
          <span class="filter-row-label">Tipo</span>
          <button
            class="filter-chip"
            :class="{ active: selectedTypes.includes('tasks') }"
            @click="toggleType('tasks')"
          >
            Tareas
          </button>
          <button
            class="filter-chip"
            :class="{ active: selectedTypes.includes('rdis') }"
            @click="toggleType('rdis')"
          >
            RDI
          </button>
          <button
            class="filter-chip"
            :class="{ active: selectedTypes.includes('additionals') }"
            @click="toggleType('additionals')"
          >
            Adicionales
          </button>
          <button
            class="filter-chip"
            :class="{ active: selectedTypes.includes('p_additionals') }"
            @click="toggleType('p_additionals')"
          >
            P. Adicionales
          </button>
          <button
            class="filter-chip"
            :class="{ active: selectedTypes.includes('communication_folios') }"
            @click="toggleType('communication_folios')"
          >
            Folios
          </button>
          <button
            class="filter-clear-btn"
            :disabled="!selectedTypes.length"
            @click="clearTypes"
          >
            Limpiar
          </button>
        </div>

				<v-divider class="my-1"></v-divider>

        <div class="filter-row">
          <button
            class="filter-chip"
            :class="{ active: selectedStatuses.includes('interactions') }"
            @click="toggleStatus('interactions')"
          >
            Buscar También en Interacciones
          </button>
        </div>
      </div>
    </transition>

    <!-- Loading State -->
    <div v-if="searching" class="state-container">
      <div class="spinner-wrap">
        <div class="spinner"></div>
      </div>
      <p class="state-label">Buscando…</p>
    </div>

    <!-- Results -->
    <div v-else class="results-body">
      <template v-if="hasResults">
        <!-- Tabs -->
        <div class="tabs-bar">
          <button
            v-for="(tab, i) in availableTabs"
            :key="i"
            class="tab-btn"
            :class="{ active: activeTab === i }"
            @click="activeTab = i"
          >
            {{ tab.label }}
            <span class="tab-count">{{ tab.count }}</span>
          </button>
        </div>

        <!-- Tab Content -->
        <div class="results-list">
          <!-- Tareas -->
          <template v-if="availableTabs[activeTab] && availableTabs[activeTab].key === 'tasks'">
            <button
              v-for="(tarea, i) in searchResult.tasks"
              :key="i"
              class="result-item"
              @click="viewTarea(tarea.id, $options.filters.seenByMe(tarea.seen_by_me))"
            >
              <div class="item-left">
								<div class="d-flex flex-column align-center justify-start" style="gap: 4px">
									<svg v-if="tarea.status == 3" width="15" height="15" viewBox="0 0 24 24" fill="green"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
									<div class="item-badge">
										<span>{{ tarea.number }}</span>
									</div>
								</div>
                <div class="item-content">
                  <p class="item-title">{{ tarea.title }}</p>
                  <p class="item-desc"><span v-html="tarea.description"></span></p>
                </div>
              </div>
              <svg class="item-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </template>

          <!-- RDI -->
          <template v-if="availableTabs[activeTab] && availableTabs[activeTab].key === 'rdis'">
            <button
              v-for="(rdi, i) in searchResult.rdis"
              :key="i"
              class="result-item"
              @click="viewRDI(rdi.id)"
            >
              <div class="item-left">
								<div class="d-flex flex-column align-center justify-start" style="gap: 4px">
									<svg v-if="rdi.status == 3" width="15" height="15" viewBox="0 0 24 24" fill="green"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
									<div class="item-badge">
										<span>{{ rdi.number }}</span>
									</div>
								</div>
                <div class="item-content">
                  <p class="item-title">{{ rdi.title }}</p>
                  <p class="item-desc"><span v-html="rdi.description"></span></p>
                </div>
              </div>
              <svg class="item-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </template>

          <!-- Adicionales -->
          <template v-if="availableTabs[activeTab] && availableTabs[activeTab].key === 'additionals'">
            <button
              v-for="(additional, i) in searchResult.additionals"
              :key="i"
              class="result-item"
              @click="viewAdditional(additional.id)"
            >
              <div class="item-left">
								<div class="d-flex flex-column align-center justify-start" style="gap: 4px">
									<svg v-if="additional.status == 3" width="15" height="15" viewBox="0 0 24 24" fill="green"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
									<div class="item-badge">
										<span>{{ additional.number }}</span>
									</div>
								</div>
                <div class="item-content">
                  <p class="item-title">{{ additional.title }}</p>
                </div>
              </div>
              <svg class="item-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </template>

          <!-- P. Adicional -->
          <template v-if="availableTabs[activeTab] && availableTabs[activeTab].key === 'p_additionals'">
            <button
              v-for="(tarea, i) in searchResult.p_additionals"
              :key="i"
              class="result-item"
              @click="viewTarea(tarea.id, $options.filters.seenByMe(tarea.seen_by_me), true)"
            >
              <div class="item-left">
								<div class="d-flex flex-column align-center justify-start" style="gap: 4px">
									<svg v-if="tarea.status == 3" width="15" height="15" viewBox="0 0 24 24" fill="green"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
									<div class="item-badge">
										<span>{{ tarea.number }}</span>
									</div>
								</div>
                <div class="item-content">
                  <p class="item-title">{{ tarea.title }}</p>
                  <p class="item-desc"><span v-html="tarea.description"></span></p>
                </div>
              </div>
              <svg class="item-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </template>

          <!-- Folios de comunicaciones -->
          <template v-if="availableTabs[activeTab] && availableTabs[activeTab].key === 'communication_folios'">
            <button
              v-for="(folio, i) in searchResult.communication_folios"
              :key="i"
              class="result-item"
              @click="viewFolio(folio.id)"
            >
              <div class="item-left">
                <div class="d-flex flex-column align-center justify-start" style="gap: 4px">
                  <svg v-if="folio.status === 'finished'" width="15" height="15" viewBox="0 0 24 24" fill="green"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                  <div class="item-badge">
                    <span>{{ folio.number }}</span>
                  </div>
                </div>
                <div class="item-content">
                  <p class="item-title">{{ folio.title }}</p>
                  <p class="item-desc"><span v-html="folio.description"></span></p>
                </div>
              </div>
              <svg class="item-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </template>

          <!-- Entregas -->
          <template v-if="availableTabs[activeTab] && availableTabs[activeTab].key === 'entregas'">
            <button
              v-for="(entrega, i) in searchResult.entregas"
              :key="i"
              class="result-item"
              @click="viewEntrega(entrega.id)"
            >
              <div class="item-left">
								<div class="d-flex flex-column align-center justify-start" style="gap: 4px">
									<svg v-if="entrega.status == 3" width="15" height="15" viewBox="0 0 24 24" fill="green"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
									<div class="item-badge">
										<span>{{ entrega.id }}</span>
									</div>
								</div>
                <div class="item-content">
                  <p class="item-title">{{ entrega.title }}</p>
                </div>
              </div>
              <svg class="item-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </template>
        </div>
      </template>

      <!-- Empty State -->
      <div v-else class="state-container">
        <div class="empty-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            <line x1="8" y1="11" x2="14" y2="11"/>
          </svg>
        </div>
        <p class="state-label">Sin resultados</p>
        <p class="state-sub">Intenta con otras palabras clave</p>
      </div>
    </div>
  </div>
</template>

<script>
import { getTareaByID } from '@/helpers/api/tasks'
import { getRdiByID } from '@/helpers/api/rdi'
import { getEntregaByID, getConfig } from '@/helpers/api/entrega'
import { getJobTypes } from '@/helpers/api/user'

export default {
  name: 'SearchBlock',
  props: {
    closeResults: { type: Function },
    searchResult: { type: Object },
    searching: { type: Boolean, default: true },
    applyFilters: { type: Function, default: () => {} }
  },
  data: () => ({
    activeTab: 0,
    showFilters: false,
    selectedStatuses: [],
    selectedTypes: []
  }),
  computed: {
    activeFilterCount() {
      return this.selectedStatuses.length + this.selectedTypes.length
    },
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },
    hasResults() {
      if (!this.searchResult) return false
      return (
        (this.searchResult.tasks?.length > 0) ||
        (this.searchResult.rdis?.length > 0) ||
        (this.searchResult.additionals?.length > 0) ||
        (this.searchResult.communication_folios?.length > 0) ||
        (this.searchResult.entregas?.length > 0)
      )
    },
    availableTabs() {
      if (!this.searchResult) return []
      const tabs = []
      if (this.searchResult.tasks?.length > 0)
        tabs.push({ key: 'tasks', label: 'Tareas', count: this.searchResult.tasks.length })
      if (this.searchResult.rdis?.length > 0)
        tabs.push({ key: 'rdis', label: 'RDI', count: this.searchResult.rdis.length })
      if (this.searchResult.additionals?.length > 0)
        tabs.push({ key: 'additionals', label: 'Adicionales', count: this.searchResult.additionals.length })
      if (this.searchResult.p_additionals?.length > 0)
        tabs.push({ key: 'p_additionals', label: 'P. Adicionales', count: this.searchResult.p_additionals.length })
      if (this.searchResult.communication_folios?.length > 0)
        tabs.push({ key: 'communication_folios', label: 'Folios', count: this.searchResult.communication_folios.length })
      if (this.searchResult.entregas?.length > 0)
        tabs.push({ key: 'entregas', label: 'Entregas', count: this.searchResult.entregas.length })
      return tabs
    }
  },
  methods: {
    toggleStatus(status) {
      const idx = this.selectedStatuses.indexOf(status)
      if (idx === -1) this.selectedStatuses.push(status)
      else this.selectedStatuses.splice(idx, 1)
      this.applyFilters(this.selectedStatuses, this.selectedTypes)
    },
    clearStatuses() {
      this.selectedStatuses = []
      this.applyFilters(this.selectedStatuses, this.selectedTypes)
    },
    toggleType(type) {
      const idx = this.selectedTypes.indexOf(type)
      if (idx === -1) this.selectedTypes.push(type)
      else this.selectedTypes.splice(idx, 1)
      this.applyFilters(this.selectedStatuses, this.selectedTypes)
    },
    clearTypes() {
      this.selectedTypes = []
      this.applyFilters(this.selectedStatuses, this.selectedTypes)
    },
    async viewTarea(task_id, seen_by_me, is_posible_additional = false) {
      this.closeResults()
      this.$store.consultasStore.commit('setCurrentTarea', { loading: true, is_posible_additional })
      const tarea = await getTareaByID({ task_id })
      if (tarea.code === 200) {
        let tareaData = tarea.task
        tareaData.prev_seen_by_me = seen_by_me
        this.$store.consultasStore.commit('setCurrentTarea', tareaData)
      }
    },
    async viewRDI(rdi_id) {
      this.$store.rdiStore.commit('setCurrentRDI', { loading: true })
      const rdi = await getRdiByID({ rdi_id })
      if (rdi.code === 200) {
        let rdiData = rdi.rdi
        rdiData.prev_seen_by_me = false
        this.$store.rdiStore.commit('setCurrentRDI', rdiData)
      }
    },
    async viewAdditional(additional_id) {
      this.closeResults()
      this.$store.adicionalesStore.dispatch('getAdditionalByID', { additional_id, seen_by_me: true })
    },
    async viewFolio(folioId) {
      this.closeResults()
      this.$store.communicationBooksStore.commit('setCurrentFolio', { loading: true })
      await this.$store.communicationBooksStore.dispatch('loadFolio', folioId)
    },
    async viewEntrega(entregaId) {
      if (!this.selectedProject) return
      this.closeResults()
      const projectID = this.selectedProject.id
      this.$store.entregaStore.commit('setCurrentEntrega', { loading: true })
      try {
        const entregaResponse = await getEntregaByID({ entrega_id: entregaId })
        const [configResponse, jobTypesResponse] = await Promise.all([
          getConfig({ project_id: projectID }),
          getJobTypes({ project_id: projectID })
        ])
        this.$store.usersStore.commit('setJobTypes', jobTypesResponse.job_types)
        this.$store.entregaStore.commit('setConfig', configResponse.config)
        if (entregaResponse.code === 403) {
          this.$store.entregaStore.commit('setCurrentEntrega', null)
        } else if (entregaResponse.code === 200) {
          this.$store.entregaStore.commit('setCurrentEntrega', entregaResponse.entrega)
        }
      } catch {
        this.$store.entregaStore.commit('setCurrentEntrega', { loading: false, error: true })
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');

* { box-sizing: border-box; }

/* ── Panel Shell ── */
.search-panel {
  font-family: 'DM Sans', sans-serif;
  background: #ffffff;
  border-radius: 16px;
  box-shadow:
    0 4px 6px -1px rgba(0,0,0,0.07),
    0 12px 32px -4px rgba(45, 75, 149, 0.18),
    0 0 0 1px rgba(45, 75, 149, 0.08);
  overflow: hidden;
}

/* ── Header ── */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 12px 16px;
  background: linear-gradient(135deg, #2D4B95 0%, #3d5fc0 100%);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 7px;
  color: rgba(255,255,255,0.9);
}

.header-icon { opacity: 0.85; }

.header-title {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255,255,255,0.95);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-toggle-btn {
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 9px;
  border-radius: 8px;
  border: none;
  background: rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.85);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.filter-toggle-btn:hover {
  background: rgba(255,255,255,0.22);
  color: #fff;
}
.filter-toggle-btn.active {
  background: #fff;
  color: #2D4B95;
}

.filter-toggle-label {
  font-family: 'DM Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.filter-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 20px;
  background: #f59e0b;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  border: 1.5px solid #2D4B95;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  border: none;
  background: rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.close-btn:hover {
  background: rgba(255,255,255,0.22);
  color: #fff;
}

/* ── Filters ── */
.filters-section {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 8px 14px;
  border-bottom: 1px solid #f0f2f7;
  background: #fafbfe;
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
}

.filter-row-label {
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-right: 1px;
  flex-shrink: 0;
  min-width: 40px;
}

.filter-chip {
  padding: 3px 9px;
  border-radius: 20px;
  border: 1px solid #e5e9f5;
  background: #ffffff;
  font-family: 'DM Sans', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  white-space: nowrap;
  line-height: 1.4;
}

.filter-chip:hover {
  border-color: #2D4B95;
  color: #2D4B95;
}

.filter-chip.active {
  background: linear-gradient(135deg, #2D4B95, #5E85EF);
  border-color: transparent;
  color: #fff;
  font-weight: 600;
}

.filter-clear-btn {
  padding: 3px 6px;
  border: none;
  background: transparent;
  font-family: 'DM Sans', sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  color: #9ca3af;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  margin-left: auto;
  transition: color 0.15s;
}

.filter-clear-btn:hover:not(:disabled) { color: #2D4B95; }

.filter-clear-btn:disabled {
  opacity: 0.4;
  cursor: default;
  text-decoration: none;
}

/* ── Filters Collapse Transition ── */
.filters-collapse-enter-active,
.filters-collapse-leave-active {
  transition: max-height 0.2s ease, opacity 0.15s ease, padding 0.2s ease;
  overflow: hidden;
}
.filters-collapse-enter,
.filters-collapse-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}
.filters-collapse-enter-to,
.filters-collapse-leave {
  max-height: 90px;
  opacity: 1;
}

/* ── Loading / Empty States ── */
.state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  gap: 8px;
}

.spinner-wrap {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #e8edf8;
  border-top-color: #2D4B95;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.empty-icon {
  width: 52px;
  height: 52px;
  background: #f0f4ff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8fa4d4;
  margin-bottom: 4px;
}

.state-label {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.state-sub {
  margin: 0;
  font-size: 12px;
  color: #9ca3af;
}

/* ── Tabs ── */
.tabs-bar {
  display: flex;
	flex-wrap: wrap;
  padding: 10px 12px 0;
  gap: 4px;
  border-bottom: 1px solid #f0f2f7;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border-radius: 8px 8px 0 0;
  border: none;
  background: transparent;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
  position: relative;
  bottom: -1px;
}

.tab-btn:hover { color: #2D4B95; background: #f5f7ff; }

.tab-btn.active {
  color: #2D4B95;
  background: #fff;
  border: 1px solid #f0f2f7;
  border-bottom-color: #fff;
  font-weight: 600;
}

.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  padding: 0 4px;
  border-radius: 20px;
  background: #eef1fb;
  color: #2D4B95;
  font-size: 10px;
  font-weight: 700;
}
.tab-btn.active .tab-count {
  background: #2D4B95;
  color: #fff;
}

/* ── Results List ── */
.results-body { }

.results-list {
  max-height: 340px;
  overflow-y: auto;
  padding: 6px 0;
}

.results-list::-webkit-scrollbar { width: 4px; }
.results-list::-webkit-scrollbar-track { background: transparent; }
.results-list::-webkit-scrollbar-thumb { background: #dde2f0; border-radius: 4px; }

/* ── Result Item ── */
.result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
  border-bottom: 1px solid #f7f8fc;
  font-family: 'DM Sans', sans-serif;
}

.result-item:last-child { border-bottom: none; }
.result-item:hover { background: #f5f7ff; }
.result-item:hover .item-arrow { color: #2D4B95; transform: translateX(2px); }

.item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.item-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  padding: 0 5px;
  border-radius: 8px;
  background: linear-gradient(135deg, #2D4B95, #5E85EF);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  letter-spacing: -0.02em;
}

.item-badge.badge-done {
  background: linear-gradient(135deg, #059669, #10b981);
}

.item-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.item-title {
  margin: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-desc {
  margin: 2px 0 0;
  font-size: 11.5px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}
/deep/ .item-desc p{
	margin: 0!important;
}

.item-arrow {
  color: #d1d5db;
  flex-shrink: 0;
  margin-left: 8px;
  transition: color 0.15s, transform 0.15s;
}
</style>